// import  { getHeaders } from "src/library/utils/auth";
import {
  FilterParams,
  GroupBy,
  PaginatedData,
  PaginatedParams
} from "src/library/utils";
import useAxios, { IApiResponse } from "src/core/shared/hooks/useAxios";

type ErrorApi = "CREATE_ORDER_ERROR" | "UPDATE_ORDER_ERROR" | "COUNT_ERRORS" | "LIST_ERRORS" | "LIST_CURRENT_ERRORS" | "RESOLVE_ORDER_ERROR";

export type CreateErrorParams = Pick<IError, "entityId" | "code" | "note">;
export type CountErrorsParams = { filters: FilterParams<IError>; countField?: GroupBy<IError> };
export type ListErrorsParams = { filters: FilterParams<IError>; pagination: PaginatedParams<IError>; groupBy?: GroupBy<IError> };
export type CountErrorsResult = { count: number };
export type ListErrorsResponse = {
  errors: PaginatedData<IError>;
  currentErrorCodes: IErrorCode[];
};

export const errorsApi = <R, P = any>(name: ErrorApi): [IApiResponse<R, any>, <C = P>(P: C) => void] => {
  switch (name) {
    case "CREATE_ORDER_ERROR":
      return useAxios<R>(
        (data: CreateErrorParams) => ({
          url: `${process.env.REACT_APP_API_URL}/seedless/errors`,
          method: "POST",
          data
        }),
        name
      );
    case "RESOLVE_ORDER_ERROR":
      return useAxios<R>(
        data => ({
          url: `${process.env.REACT_APP_API_URL}/seedless/errors/${data.id}/resolve`,
          method: "PATCH"
        }),
        name
      );
    case "COUNT_ERRORS":
      return useAxios<R>(
        (params: CountErrorsParams) => ({
          url: `${process.env.REACT_APP_API_URL}/core/errors/count/all`,
          method: "GET",
          params
        }),
        name
      );
    case "LIST_ERRORS":
      return useAxios<R>((params: ListErrorsParams) => {
        let groupBy;

        if (params.groupBy) {
          groupBy = JSON.stringify(params.groupBy);
        }

        return {
          url: `${process.env.REACT_APP_API_URL}/core/errors`,
          method: "GET",
          params: {
            ...params,
            groupBy
          }
        };
      }, name);
    case "LIST_CURRENT_ERRORS":
      return useAxios<R>((params: Pick<ListErrorsParams, "filters" | "pagination">) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/core/errors/current`,
          method: "GET",
          params
        };
      }, name);
    case "UPDATE_ORDER_ERROR":
      return useAxios<R>(
        data => ({
          url: `${process.env.REACT_APP_API_URL}/seedless/errors/${data.id}/resolve`,
          method: "PATCH"
        }),
        name
      );

    default:
      throw new Error("errorsApi requires a name parameter");
  }
};

export enum ErrorResolutionCode {
  AutoRetry = "auto_retry",
  ActionRequiredRetry = "action_required_retry",
  ManuallyResolve = "manually_resolve",
  SystemAdminAction = "system_admin_action"
}

export interface IError<T = any> {
  id?: number;
  createdAt?: Date;
  updatedAt?: Date;
  note?: string;
  code: ErrorCodeCode;
  status: ErrorStatus;
  entityId: number;
  type: ErrorType;
  errorCode?: IErrorCode;
  secondaryEntityId?: number;
  secondaryType?: ErrorSecondaryType;
  data?: T;
  resolutionCode?: ErrorResolutionCode;
}

export type ErrorStatus = "active" | "resolved" | "deprecated";

export interface IListErrorData {
  reason: string;
  deliveryDate: Date;
}

export interface IOrderLineError extends IError {
  secondaryType: "order-line";
}

export interface IErrorCode {
  id?: number;
  note: string;
  description: string;
  code: ErrorCodeCode;
  type: ErrorType;
  secondaryType?: ErrorSecondaryType;
  originatedFrom: ErrorCodeOriginatedFromType;
}

export type ErrorType = "credit" | "order" | "list";
export type ErrorSecondaryType = "credit-line" | "order-line" | "list-line";
export type ErrorCodeOriginatedFromType =
  | "erp"
  | "erp-submit"
  | "erp-cancel"
  | "erp-resync"
  | "manual-customer"
  | "manual-admin"
  | "webhook"
  | "system"
  | "ui";

export type ErrorCodeCode =
  | "manual-order-line"
  | "manual-order"
  | "product-not-available"
  | "delivery-date-error"
  | "unityf8-error"
  | "standing-order-error"
  | "delivery-date-error"
  | "unityf8-error-place"
  | "unityf8-error-cancel"
  | "unityf8-error-resync";
